import React from 'react';
import Select from 'react-select';
import { Controller, useForm } from 'react-hook-form';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuill } from 'react-quilljs';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Spinner from './spinner/Spinner';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

const schema = yup
  .object({
    title: yup.string().required('Title is required'),
    target_type: yup
      .object()
      .shape({
        label: yup.string().required(),
        value: yup.string().required(),
      })
      .required('Target type is required'),
    priority: yup
      .object()
      .shape({
        label: yup.string().required(),
        value: yup.string().required(),
      })
      .required('Priority is required'),
    notification_type: yup
      .object()
      .shape({
        label: yup.string().required(),
        value: yup.string().required(),
      })
      .required('Notification type is required'),
    cohort: yup
      .object()
      .nullable()
      .when('target_type', {
        is: (val) => val?.value === 'cohort' || val?.value === 'module',
        then: () => yup.object().required('Cohort is required'),
      }),
    module: yup
      .object()
      .nullable()
      .when('target_type', {
        is: (val) => val?.value === 'module',
        then: () => yup.object().required('Module is required'),
      }),
  })
  .required();

const targetTypeOptions = [
  { value: 'all', label: 'All Students' },
  { value: 'cohort', label: 'Specific Cohort' },
  { value: 'module', label: 'Specific Module' },
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const typeOptions = [
  { value: 'general', label: 'General Announcement' },
  { value: 'assignment', label: 'Assignment Update' },
  { value: 'class', label: 'Class Session' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'resource', label: 'New Resource' },
];

function CreateNotification({ toggleModal }) {
  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
      ],
      clipboard: {
        matchVisual: false,
      },
    },
    placeholder: 'Type notification message...',
  });

  const { userDetails } = useSelector((state) => state.auth);
  const isAdmin =
    userDetails?.role === 'Admin' ||
    userDetails?.role === 'admin' ||
    userDetails?.role === 'super_admin' ||
    userDetails?.role === 'SuperAdmin';
  const isModuleLead = userDetails?.role === 'ModuleLead';

  // For Module Leads, only allow targeting specific modules
  const filteredTargetTypeOptions = isAdmin
    ? targetTypeOptions
    : targetTypeOptions.filter((opt) => opt.value === 'module');

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      priority: priorityOptions[1], // Medium
      notification_type: typeOptions[0], // General
      target_type: filteredTargetTypeOptions[0],
    },
  });

  const selectedTargetType = watch('target_type');
  const selectedCohort = watch('cohort');

  const queryClient = useQueryClient();

  // Fetch assigned modules for Module Lead
  const { data: assignedModulesData } = useFetchDatav2(
    ['module-instructors', userDetails?.id],
    '/module-instructors',
    {
      params: {
        instructor_id: userDetails?.id,
      },
    },
    "Couldn't get assigned modules",
    isModuleLead
  );

  // Get the list of module IDs assigned to this Module Lead
  const assignedModuleIds =
    assignedModulesData?.data?.module_instructors?.map((mi) => mi.module_id) ||
    [];

  // Fetch Cohorts
  const { data: cohortsData } = useFetchDatav2(
    ['cohorts'],
    '/cohorts',
    {},
    "Couldn't get cohorts",
    selectedTargetType?.value === 'cohort' ||
      selectedTargetType?.value === 'module'
  );

  // For Module Leads, filter cohorts to only those that contain their assigned modules
  const cohortOptions = isModuleLead
    ? cohortsData?.data?.cohorts
        ?.filter((cohort) =>
          cohort.modules?.some((modId) => assignedModuleIds.includes(modId))
        )
        .map((cohort) => ({
          value: cohort.id,
          label: `${cohort.name}`,
          modules: cohort.modules?.filter((modId) =>
            assignedModuleIds.includes(modId)
          ),
        })) || []
    : cohortsData?.data?.cohorts?.map((cohort) => ({
        value: cohort.id,
        label: `${cohort.name}`,
        modules: cohort.modules,
      })) || [];

  // Fetch Modules (filtered by cohort if selected)
  const { data: modulesData } = useFetchDatav2(
    ['modules', selectedCohort?.value],
    '/course-modules',
    {
      params: {
        modules_ids: selectedCohort?.modules
          ? JSON.stringify(selectedCohort.modules)
          : undefined,
      },
    },
    "Couldn't get modules",
    selectedTargetType?.value === 'module' && selectedCohort?.value
  );

  // For Module Leads, filter module options to only their assigned modules
  const moduleOptions = isModuleLead
    ? modulesData?.data?.course_modules
        ?.filter((module) => assignedModuleIds.includes(module.id))
        .map((module) => ({
          value: module.id,
          label: module.moduleName,
        })) || []
    : modulesData?.data?.course_modules?.map((module) => ({
        value: module.id,
        label: module.moduleName,
      })) || [];

  const onSendSuccess = () => {
    toast.success('Notification sent successfully');
    toggleModal();
    queryClient.invalidateQueries(['notifications']);
  };

  const onSendFailure = (error) => {
    toast.error(
      error?.response?.data?.message || 'Failed to send notification'
    );
  };

  const { mutate, isLoading } = useMutation(
    (notificationData) => {
      return axiosInstancev2.post(`/notifications/push`, notificationData);
    },
    { onSuccess: onSendSuccess, onError: onSendFailure }
  );

  const onSubmit = async (data) => {
    if (!quill || quill.getText().trim().length === 0) {
      toast.error('Message content is required');
      return;
    }

    const payload = {
      title: data.title,
      message: quill.getText(), // Plain text for push
      html_content: quill.root.innerHTML, // Rich text for in-app
      type: data.notification_type.value,
      priority: data.priority.value,
      target_type: data.target_type.value,
      target_ids: [],
      is_push_enabled: true,
      is_in_app_enabled: true,
    };

    if (data.target_type.value === 'cohort' && data.cohort) {
      payload.target_ids = [data.cohort.value];
    } else if (data.target_type.value === 'module' && data.module) {
      payload.target_ids = [data.module.value];
    }

    mutate(payload);
  };

  return (
    <form className="text-gray-700 space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Title */}
        <div className="flex flex-col space-y-1">
          <label htmlFor="title" className="text-sm font-medium">
            Notification Title
          </label>
          <input
            {...register('title')}
            type="text"
            id="title"
            placeholder="Enter title"
            className={`
                border-[#CBCBCB]
                border-[1px]
                px-3
                py-2
                rounded-lg
                focus:outline-none
                focus:border-claret-500
                ${errors.title ? 'border-red-600' : ''}
              `.trim()}
          />
          {errors.title && (
            <span className="text-red-600 text-xs">{errors.title.message}</span>
          )}
        </div>

        {/* Notification Type */}
        <div className="flex flex-col space-y-1">
          <label className="text-sm font-medium">Type</label>
          <Controller
            name="notification_type"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={typeOptions}
                className="react-select"
                classNamePrefix="select"
                placeholder="Select Type"
              />
            )}
          />
          {errors.notification_type && (
            <span className="text-red-600 text-xs">
              {errors.notification_type.message}
            </span>
          )}
        </div>

        {/* Priority */}
        <div className="flex flex-col space-y-1">
          <label className="text-sm font-medium">Priority</label>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={priorityOptions}
                className="react-select"
                classNamePrefix="select"
                placeholder="Select Priority"
              />
            )}
          />
          {errors.priority && (
            <span className="text-red-600 text-xs">
              {errors.priority.message}
            </span>
          )}
        </div>

        {/* Target Type */}
        <div className="flex flex-col space-y-1">
          <label className="text-sm font-medium">Target Audience</label>
          <Controller
            name="target_type"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={filteredTargetTypeOptions}
                className="react-select"
                classNamePrefix="select"
                placeholder="Select Target"
                onChange={(val) => {
                  field.onChange(val);
                  setValue('cohort', null);
                  setValue('module', null);
                }}
              />
            )}
          />
          {errors.target_type && (
            <span className="text-red-600 text-xs">
              {errors.target_type.message}
            </span>
          )}
        </div>

        {/* Target Selector */}
        {(selectedTargetType?.value === 'cohort' ||
          selectedTargetType?.value === 'module') && (
          <div className="flex flex-col space-y-1 md:col-span-2">
            <label className="text-sm font-medium">Select Cohort</label>
            <Controller
              name="cohort"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={cohortOptions}
                  className="react-select"
                  classNamePrefix="select"
                  placeholder="Select Cohort"
                  isLoading={!cohortOptions.length}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue('module', null);
                  }}
                />
              )}
            />
            {errors.cohort && (
              <span className="text-red-600 text-xs">
                {errors.cohort.message}
              </span>
            )}
          </div>
        )}

        {selectedTargetType?.value === 'module' && selectedCohort && (
          <div className="flex flex-col space-y-1 md:col-span-2">
            <label className="text-sm font-medium">Select Module</label>
            <Controller
              name="module"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={moduleOptions}
                  className="react-select"
                  classNamePrefix="select"
                  placeholder="Select Module"
                  isLoading={!moduleOptions.length && !!selectedCohort}
                />
              )}
            />
            {errors.module && (
              <span className="text-red-600 text-xs">
                {errors.module.message}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Message Editor */}
      <div className="flex flex-col space-y-1 mt-4">
        <label className="text-sm font-medium">Message Content</label>
        <div
          className="border border-gray-300 rounded-lg overflow-hidden"
          style={{ minHeight: '200px' }}
        >
          <div ref={quillRef} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 mt-6">
        <button
          type="button"
          onClick={toggleModal}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <CancelIcon className="mr-2" fontSize="small" />
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center px-6 py-2 bg-claret-500 text-white rounded-lg hover:bg-claret-600 transition-colors disabled:opacity-70"
        >
          {isLoading ? (
            <Spinner size="sm" color="white" />
          ) : (
            <>
              <SendIcon className="mr-2" fontSize="small" />
              Send Notification
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default CreateNotification;
